import AttachmentController from '@controllers/AttachmentController.js'
import PdfExportController from '@controllers/PdfExportController.js'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function usePdfExport() {
  const generatingPdf = ref(false)
  const toast = useToast()
  const { t } = useI18n()

  /**
   * Generate a temporary link and trigger a download to avoid popup blockers.
   * @param url
   */
  const triggerDownload = (url) => {
    const link = document.createElement('a')
    link.href = url
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  /**
   * @param {{ template: string, id?: string, filter?: string }} options
   */
  const generatePdf = async ({ template, id = undefined, filter = undefined }) => {
    generatingPdf.value = true
    toast.add({
      severity: 'info',
      summary: t('export.summary'),
      detail: t('export.detail'),
    })

    try {
      const url = PdfExportController.generatePdf(
        { template },
        {
          query: {
            id: id || undefined,
            filter: filter || undefined,
          },
        },
      ).url

      const response = await axios.get(url)

      toast.removeAllGroups()
      triggerDownload(AttachmentController.download(response.data.export).url)
    } catch {
      toast.removeAllGroups()
      toast.add({
        severity: 'error',
        summary: t('export.error.summary'),
        detail: t('export.error.detail'),
        life: 5000,
      })
    } finally {
      generatingPdf.value = false
    }
  }

  /**
   * @param {{ template: string, id?: string, filter?: string }} options
   */
  const previewPdf = async ({ template, id = undefined, filter = undefined }) => {
    try {
      const url = PdfExportController.previewPdf(
        { template },
        {
          query: {
            id: id || undefined,
            filter: filter || undefined,
          },
        },
      ).url

      const response = await axios.get(url)
      window.open(response.data.url, '_blank')
    } catch {
      toast.add({
        severity: 'error',
        summary: t('export.preview_error.summary'),
        detail: t('export.preview_error.detail'),
        life: 5000,
      })
    }
  }

  return {
    generatePdf,
    previewPdf,
    generatingPdf,
  }
}
